import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authService } from '../services/authService'
import type { 
  UseAuthReturn, 
  UseAuthCheckReturn, 
  UseRequireAuthReturn, 
  UseRoleCheckReturn,
  UserRole 
} from '../types/auth'

export const useAuth = (): UseAuthReturn => {
  const { 
    user, 
    isAuthenticated, 
    isLoading,
    login, 
    loginWithToken, 
    logout,
    refreshToken,
    setUser,
    clearAuth
  } = useAuthStore()
  
  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithToken,
    logout,
    refreshToken,
    setUser,
    clearAuth
  }
}

export const useAuthCheck = (): UseAuthCheckReturn => {
  const navigate = useNavigate()
  const { isAuthenticated, loginWithToken, setUser, clearAuth } = useAuthStore()
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for URL token first
        const urlToken = authService.checkUrlToken()
        if (urlToken) {
          try {
            await loginWithToken(urlToken)
            navigate('/app')
            return
          } catch (error) {
            console.error('Invalid URL token:', error)
          }
        }
        
        // Check existing token
        const storedToken = authService.getStoredToken()
        if (storedToken && !isAuthenticated) {
          // Check if token is likely expired before making API call
          if (authService.isTokenLikelyExpired(storedToken)) {
            console.log('Token appears expired, clearing auth')
            clearAuth()
            return
          }
          
          try {
            authService.setAuthToken(storedToken)
            const user = await authService.getCurrentUser()
            setUser(user, storedToken)
          } catch (error) {
            console.error('Token validation failed:', error)
            clearAuth()
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        clearAuth()
      }
    }
    
    checkAuth()
  }, [isAuthenticated, loginWithToken, setUser, clearAuth, navigate])
  
  return { isAuthenticated }
}

export const useRequireAuth = (requiredRole?: UserRole): UseRequireAuthReturn => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    } else if (requiredRole && user?.role !== requiredRole) {
      navigate('/unauthorized')
    }
  }, [isAuthenticated, user, requiredRole, navigate])
  
  return { user, isAuthenticated }
}

export const useRoleCheck = (): UseRoleCheckReturn => {
  const { user } = useAuthStore()
  
  const hasRole = (role: UserRole) => user?.role === role
  const hasAnyRole = (roles: UserRole[]) => user?.role && roles.includes(user.role)
  const canAccessEvent = (eventId: string) => {
    if (!user) return false
    if (user.role === 'admin') return true
    return user.event_ids.includes(eventId)
  }
  
  return {
    user,
    hasRole,
    hasAnyRole,
    canAccessEvent,
    isAdmin: hasRole('admin'),
    isPlanner: hasRole('planner'),
    isSupplier: hasRole('supplier'),
    isVenueManager: hasRole('venue_manager')
  }
}